import React from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from 'react-intl';
import bindAll from 'lodash.bindall';
import * as tf from '@tensorflow/tfjs';
import * as mobilenet from '@tensorflow-models/mobilenet';
import * as knnClassifier from '@tensorflow-models/knn-classifier';

import mlDb from '../../lib/ml/db';

import styles from './ml-environment.css';
import backIcon from '../../lib/assets/icon--back.svg';

class MlSandbox extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, [
            'handleBack',
            'startWebcam',
            'stopWebcam',
            'handleAddClass',
            'handleAddSample',
            'handleTrainModel',
            'handleStartPredicting',
            'handleStopPredicting',
            'predictLoop',
            'handleExport'
        ]);
        
        this.videoRef = React.createRef();
        this.previewVideoRef = React.createRef();
        this.classifier = knnClassifier.create();
        this.mobilenetModule = null;
        this.predictRAF = null;

        this.state = {
            classes: [
                {id: 1, name: 'Class 1', samples: 0},
                {id: 2, name: 'Class 2', samples: 0}
            ],
            isModelLoaded: false,
            isTrained: false,
            isPredicting: false,
            prediction: null
        };
    }

    componentDidMount () {
        this.startWebcam();
        this.loadModel();
    }

    componentWillUnmount () {
        this.stopWebcam();
    }

    async loadModel () {
        await tf.ready();
        this.mobilenetModule = await mobilenet.load({version: 2, alpha: 1.0});
        this.setState({isModelLoaded: true});
    }

    async startWebcam () {
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {facingMode: 'user'},
                    audio: false
                });
                if (this.videoRef.current) {
                    this.videoRef.current.srcObject = stream;
                    this.videoRef.current.play();
                }
                if (this.previewVideoRef.current) {
                    this.previewVideoRef.current.srcObject = stream;
                    this.previewVideoRef.current.play();
                }
            } catch (err) {
                console.error('Error accessing webcam:', err);
            }
        }
    }

    stopWebcam () {
        if (this.videoRef.current && this.videoRef.current.srcObject) {
            const tracks = this.videoRef.current.srcObject.getTracks();
            tracks.forEach(track => track.stop());
            this.videoRef.current.srcObject = null;
        }
        if (this.previewVideoRef.current) {
            this.previewVideoRef.current.srcObject = null;
        }
    }

    handleAddClass () {
        const newId = this.state.classes.length > 0 ?
            Math.max(...this.state.classes.map(c => c.id)) + 1 :
            1;
        this.setState(prev => ({
            classes: [...prev.classes, {id: newId, name: `Class ${newId}`, samples: 0}]
        }));
    }

    handleAddSample (e) {
        const classId = Number(e.currentTarget.getAttribute('data-id'));
        if (!this.mobilenetModule || !this.videoRef.current) return;
        
        // Capture frame from video
        const img = tf.browser.fromPixels(this.videoRef.current);
        const activation = this.mobilenetModule.infer(img, true);
        
        // Add to KNN
        this.classifier.addExample(activation, classId);
        
        // Update state
        this.setState(prev => ({
            classes: prev.classes.map(c =>
                (c.id === classId ? {...c, samples: c.samples + 1} : c)
            )
        }));
        
        img.dispose();
    }

    handleTrainModel () {
        const totalSamples = this.state.classes.reduce((sum, c) => sum + c.samples, 0);
        if (totalSamples > 0) {
            this.setState({isTrained: true});
        } else {
            console.warn('Add samples to classes before training!');
        }
    }

    handleStartPredicting () {
        if (!this.state.isTrained) return;
        this.setState({isPredicting: true}, () => {
            this.predictLoop();
        });
    }

    handleStopPredicting () {
        this.setState({isPredicting: false, prediction: null});
        if (this.predictRAF) {
            cancelAnimationFrame(this.predictRAF);
        }
    }

    async predictLoop () {
        if (!this.state.isPredicting || !this.previewVideoRef.current) return;
        
        if (this.classifier.getNumClasses() > 0) {
            const img = tf.browser.fromPixels(this.previewVideoRef.current);
            const activation = this.mobilenetModule.infer(img, 'conv_preds');
            try {
                const result = await this.classifier.predictClass(activation);
                const className = this.state.classes.find(c => c.id === Number(result.label))?.name || result.label;
                this.setState({prediction: {className, conf: result.confidences[result.label]}});
            } catch (e) {
                console.error(e);
            }
            img.dispose();
        }
        
        this.predictRAF = requestAnimationFrame(this.predictLoop);
    }

    async handleExport () {
        if (!this.state.isTrained) return;

        // Serialize KNN dataset
        const dataset = this.classifier.getClassifierDataset();
        const datasetObj = {};
        Object.keys(dataset).forEach(key => {
            const tensor = dataset[key];
            datasetObj[key] = {
                data: Array.from(tensor.dataSync()),
                shape: tensor.shape
            };
        });

        // Save to IndexedDB
        const updatedProject = {
            ...this.props.project,
            classes: this.state.classes,
            modelData: datasetObj,
            lastUpdated: Date.now()
        };

        try {
            await mlDb.saveProject(updatedProject);
            this.handleBack(); // Return to dashboard, which will trigger a reload or can simply navigate back.
        } catch (err) {
            console.error('Failed to save project model:', err);
        }
    }

    handleBack () {
        this.props.onBack();
    }

    render () {
        const {project} = this.props;

        return (
            <div className={styles.mlSandbox}>
                {/* Sandbox Header */}
                <div className={styles.mlNavbar}>
                    <div className={styles.mlNavbarLeft}>
                        <span className={styles.mlNavbarTitle}>
                            {project.name}
                        </span>
                    </div>
                    <div className={styles.mlNavbarRight}>
                        <button
                            className={styles.mlBackBtn}
                            onClick={this.handleBack}
                        >
                            <img
                                src={backIcon}
                                className={styles.mlBackArrow}
                            />
                            <FormattedMessage
                                id="gui.mlEnvironment.backToDashboard"
                                defaultMessage="Back to Dashboard"
                            />
                        </button>
                    </div>
                </div>

                {/* Sandbox Body */}
                <div className={styles.mlSandboxBody}>
                    <div className={styles.mlSandboxColumn}>
                        <h3>{'Data Collection'}</h3>
                        <div className={styles.mlWebcamContainer}>
                            <video
                                ref={this.videoRef}
                                autoPlay
                                playsInline
                                muted
                                width="100%"
                                height="100%"
                                style={{objectFit: 'cover', borderRadius: '8px'}}
                            />
                        </div>
                        <div className={styles.mlClassesList}>
                            {this.state.classes.map(c => (
                                <div
                                    key={c.id}
                                    className={styles.mlClassCard}
                                >
                                    <h4>{c.name}</h4>
                                    <button
                                        data-id={c.id}
                                        onClick={this.handleAddSample}
                                    >
                                        {'Add Samples ('}{c.samples}{')'}
                                    </button>
                                </div>
                            ))}
                            <button onClick={this.handleAddClass}>{'+ Add Class'}</button>
                        </div>
                    </div>

                    <div className={styles.mlSandboxColumn}>
                        <h3>{'Training'}</h3>
                        <button
                            className={styles.mlTrainButton}
                            onClick={this.handleTrainModel}
                        >
                            {this.state.isTrained ? 'Model Trained!' : 'Train Model'}
                        </button>
                        <div className={styles.mlTrainingGraph}>
                            {this.state.isTrained ?
                                '✅ Training Complete (KNN)' :
                                'Add samples and click Train'}
                        </div>
                    </div>

                    <div className={styles.mlSandboxColumn}>
                        <h3>{'Preview'}</h3>
                        <div className={styles.mlPreviewContainer}>
                            <video
                                ref={this.previewVideoRef}
                                autoPlay
                                playsInline
                                muted
                                width="100%"
                                height="100%"
                                style={{objectFit: 'cover', borderRadius: '8px'}}
                            />
                        </div>
                        {this.state.isTrained && (
                            <div style={{marginBottom: '16px'}}>
                                <button
                                    className={styles.mlToolbarBtn}
                                    onClick={this.state.isPredicting ?
                                        this.handleStopPredicting :
                                        this.handleStartPredicting}
                                >
                                    {this.state.isPredicting ? 'Stop Predicting' : 'Start Predicting'}
                                </button>
                            </div>
                        )}
                        {this.state.prediction && (
                            <div style={{marginBottom: '16px', fontWeight: 'bold', color: '#6842c2'}}>
                                {'Prediction: '}{this.state.prediction.className}
                                {' ('}{Math.round(this.state.prediction.conf * 100)}{'%)'}
                            </div>
                        )}
                        <button
                            className={styles.mlExportButton}
                            onClick={this.handleExport}
                            disabled={!this.state.isTrained}
                            style={{opacity: this.state.isTrained ? 1 : 0.5}}
                        >
                            {'Export to Scratch'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}

MlSandbox.propTypes = {
    onBack: PropTypes.func.isRequired,
    project: PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        type: PropTypes.string
    }).isRequired
};

export default MlSandbox;
